import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { 
  X, 
  Lightbulb, 
  Vote, 
  Zap, 
  Star,
  FileText,
  Link as LinkIcon,
  Upload,
  ThumbsUp,
  Trophy,
  Sparkles,
  Users,
  CheckCircle
} from "lucide-react";
import { stripMarkdownFormatting } from "@/lib/stripMarkdown";

interface StageReviewPanelProps {
  sprintId: string;
  stageName: 'inform' | 'decide' | 'execute' | 'assess';
  onClose: () => void;
}

interface InformContent {
  id: string;
  contributor_id: string;
  content_type: string;
  content_reference: string;
  ai_summary: string | null;
  key_concepts: any;
  profiles?: { display_name: string; avatar_url: string | null };
}

interface Decision {
  id: string;
  decision_text: string;
  rationale: string;
  ai_confidence_score: number | null;
  contributor_votes: Record<string, boolean> | null;
  is_selected: boolean;
}

interface Contribution {
  id: string;
  contributor_id: string;
  contribution_text: string;
  prompt_used: string | null;
  ai_output: string | null;
  contributor_rating: number | null;
}

interface Assessment {
  id: string;
  contribution_id: string;
  assessor_id: string;
  rating_action: string;
  feedback_text: string | null;
}

interface ContributorProfile {
  display_name: string;
  avatar_url: string | null;
}

const stageConfig = {
  inform: { icon: Lightbulb, title: 'Inform Stage', color: 'text-blue-500' },
  decide: { icon: Vote, title: 'Decide Stage', color: 'text-purple-500' },
  execute: { icon: Zap, title: 'Execute Stage', color: 'text-orange-500' },
  assess: { icon: Star, title: 'Assess Stage', color: 'text-green-500' },
};

const contentTypeIcons: Record<string, any> = {
  file: Upload,
  kb_link: LinkIcon,
  text: FileText,
  url: LinkIcon,
};

export const StageReviewPanel = ({ sprintId, stageName, onClose }: StageReviewPanelProps) => {
  const [loading, setLoading] = useState(true);
  const [informContent, setInformContent] = useState<InformContent[]>([]);
  const [decisions, setDecisions] = useState<Decision[]>([]);
  const [contributions, setContributions] = useState<Contribution[]>([]);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [contributorProfiles, setContributorProfiles] = useState<Record<string, ContributorProfile>>({});

  useEffect(() => {
    loadStageData();
  }, [sprintId, stageName]);

  const loadStageData = async () => {
    setLoading(true);
    try {
      switch (stageName) {
        case 'inform':
          const { data: informData } = await supabase
            .from('sprint_inform_content')
            .select(`
              *,
              profiles:contributor_id (display_name, avatar_url)
            `)
            .eq('sprint_id', sprintId)
            .order('created_at', { ascending: true });
          setInformContent(informData || []);
          break;

        case 'decide':
          const { data: decideData } = await supabase
            .from('sprint_decisions')
            .select('*')
            .eq('sprint_id', sprintId)
            .order('ai_confidence_score', { ascending: false });
          // Cast contributor_votes to the correct type
          const typedDecisions = (decideData || []).map(d => ({
            ...d,
            contributor_votes: (d.contributor_votes as Record<string, boolean> | null) || {}
          }));
          setDecisions(typedDecisions);
          break;

        case 'execute':
        case 'assess':
          // Load contributions
          const { data: contribData } = await supabase
            .from('sprint_contributions')
            .select('*')
            .eq('sprint_id', sprintId)
            .eq('stage', 'execute')
            .order('created_at', { ascending: true });
          setContributions(contribData || []);
          
          // Load contributor profiles
          if (contribData && contribData.length > 0) {
            const contributorIds = [...new Set(contribData.map(c => c.contributor_id))];
            const { data: profilesData } = await supabase
              .from('profiles')
              .select('id, display_name, avatar_url')
              .in('id', contributorIds);
            
            const profileMap: Record<string, ContributorProfile> = {};
            (profilesData || []).forEach(p => {
              profileMap[p.id] = { display_name: p.display_name, avatar_url: p.avatar_url };
            });
            setContributorProfiles(profileMap);
          }
          
          // For assess stage, also load assessments
          if (stageName === 'assess' && contribData && contribData.length > 0) {
            const contribIds = contribData.map(c => c.id);
            const { data: assessData } = await supabase
              .from('sprint_assessments')
              .select('*')
              .in('contribution_id', contribIds);
            setAssessments(assessData || []);
            
            // Load assessor profiles
            if (assessData && assessData.length > 0) {
              const assessorIds = [...new Set(assessData.map(a => a.assessor_id))];
              const { data: assessorProfiles } = await supabase
                .from('profiles')
                .select('id, display_name, avatar_url')
                .in('id', assessorIds);
              
              const updatedProfiles = { ...contributorProfiles };
              (assessorProfiles || []).forEach(p => {
                updatedProfiles[p.id] = { display_name: p.display_name, avatar_url: p.avatar_url };
              });
              setContributorProfiles(updatedProfiles);
            }
          }
          break;
      }
    } catch (error) {
      console.error('Error loading stage data:', error);
    } finally {
      setLoading(false);
    }
  };

  const config = stageConfig[stageName];
  const Icon = config.icon;

  const renderContributorBadge = (contributorId: string, profileOverride?: { display_name: string; avatar_url: string | null }) => {
    const profile = profileOverride || contributorProfiles[contributorId];
    const displayName = profile?.display_name || 'Anonymous';
    const initials = displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    
    return (
      <div className="flex items-center gap-2">
        <Avatar className="h-6 w-6">
          {profile?.avatar_url && <AvatarImage src={profile.avatar_url} />}
          <AvatarFallback className="text-xs">{initials}</AvatarFallback>
        </Avatar>
        <span className="text-sm font-medium">{displayName}</span>
      </div>
    );
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-8 w-8" />
        </div>
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Icon className={`w-6 h-6 ${config.color}`} />
          <h3 className="text-xl font-bold">{config.title} Review</h3>
          <Badge variant="secondary">Read-only</Badge>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="w-5 h-5" />
        </Button>
      </div>

      <div className="space-y-4 max-h-[60vh] overflow-y-auto">
        {/* Inform Stage Content */}
        {stageName === 'inform' && (
          informContent.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No content was added in this stage.</p>
          ) : (
            informContent.map((item) => {
              const ContentIcon = contentTypeIcons[item.content_type] || FileText;
              return (
                <Card key={item.id} className="p-4 space-y-3 bg-muted/30">
                  <div className="flex items-center justify-between">
                    {renderContributorBadge(item.contributor_id, item.profiles)}
                    <Badge variant="outline" className="flex items-center gap-1">
                      <ContentIcon className="w-3 h-3" />
                      {item.content_type}
                    </Badge>
                  </div>
                  
                  <div className="bg-background rounded-lg p-3 text-sm">
                    <p className="break-words">{item.content_reference}</p>
                  </div>
                  
                  {item.ai_summary && (
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                        <Sparkles className="w-3 h-3" /> AI Summary
                      </p>
                      <p className="text-sm text-muted-foreground">{item.ai_summary}</p>
                    </div>
                  )}
                  
                  {item.key_concepts && Array.isArray(item.key_concepts) && item.key_concepts.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {item.key_concepts.map((concept: any, idx: number) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {typeof concept === 'string' ? concept : concept.concept}
                        </Badge>
                      ))}
                    </div>
                  )}
                </Card>
              );
            })
          )
        )}

        {/* Decide Stage Content */}
        {stageName === 'decide' && (
          decisions.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No decisions were generated in this stage.</p>
          ) : (
            decisions.map((decision) => {
              const voteCount = Object.values(decision.contributor_votes || {}).filter(Boolean).length;
              const maxVotes = Math.max(...decisions.map(d => 
                Object.values(d.contributor_votes || {}).filter(Boolean).length
              ));
              const isTopChoice = voteCount > 0 && voteCount === maxVotes;
              
              return (
                <Card 
                  key={decision.id} 
                  className={`p-4 space-y-3 ${decision.is_selected ? 'ring-2 ring-primary bg-primary/5' : 'bg-muted/30'}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold">{decision.decision_text}</h4>
                        {decision.is_selected && (
                          <Badge className="flex items-center gap-1 bg-primary">
                            <CheckCircle className="w-3 h-3" />
                            Selected
                          </Badge>
                        )}
                        {isTopChoice && !decision.is_selected && (
                          <Badge variant="secondary" className="flex items-center gap-1">
                            <Trophy className="w-3 h-3" />
                            Top Voted
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{decision.rationale}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between pt-2 border-t">
                    <div className="flex items-center gap-3">
                      <div className="text-sm text-muted-foreground flex items-center gap-1">
                        <ThumbsUp className="w-4 h-4" />
                        {voteCount} {voteCount === 1 ? 'vote' : 'votes'}
                      </div>
                    </div>
                    
                    {decision.ai_confidence_score && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">AI Confidence:</span>
                        <Badge variant="secondary" className="flex items-center gap-1">
                          <Sparkles className="w-3 h-3" />
                          {Math.round(decision.ai_confidence_score * 100)}%
                        </Badge>
                      </div>
                    )}
                  </div>
                </Card>
              );
            })
          )
        )}

        {/* Execute Stage Content */}
        {stageName === 'execute' && (
          contributions.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No contributions were made in this stage.</p>
          ) : (
            contributions.map((contribution) => (
              <Card key={contribution.id} className="p-4 space-y-3 bg-muted/30">
                <div className="flex items-center justify-between">
                  {renderContributorBadge(contribution.contributor_id)}
                  {contribution.contributor_rating !== null && (
                    <Badge variant="secondary" className="flex items-center gap-1">
                      <Star className="w-3 h-3" />
                      Rating: {contribution.contributor_rating.toFixed(1)}
                    </Badge>
                  )}
                </div>
                
                {contribution.prompt_used && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground">Prompt Used:</p>
                    <div className="bg-background rounded-lg p-3 text-sm">
                      <p className="break-words">{contribution.prompt_used}</p>
                    </div>
                  </div>
                )}
                
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">Contribution:</p>
                  <div className="bg-background rounded-lg p-3 text-sm">
                    <p className="break-words whitespace-pre-wrap">{contribution.contribution_text}</p>
                  </div>
                </div>
                
                {contribution.ai_output && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> AI Output
                    </p>
                    <div 
                      className="bg-background rounded-lg p-3 text-sm prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: stripMarkdownFormatting(contribution.ai_output) }}
                    />
                  </div>
                )}
              </Card>
            ))
          )
        )}

        {/* Assess Stage Content */}
        {stageName === 'assess' && (
          contributions.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No assessments were made in this stage.</p>
          ) : (
            contributions.map((contribution) => {
              const contributionAssessments = assessments.filter(a => a.contribution_id === contribution.id);
              
              return (
                <Card key={contribution.id} className="p-4 space-y-3 bg-muted/30">
                  <div className="flex items-center justify-between">
                    {renderContributorBadge(contribution.contributor_id)}
                    <Badge variant="outline">
                      {contributionAssessments.length} assessment{contributionAssessments.length !== 1 ? 's' : ''}
                    </Badge>
                  </div>
                  
                  <div className="bg-background rounded-lg p-3 text-sm">
                    <p className="break-words line-clamp-2">{contribution.contribution_text}</p>
                  </div>
                  
                  {contributionAssessments.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground">Team Feedback:</p>
                      {contributionAssessments.map((assessment) => (
                        <div key={assessment.id} className="flex items-start gap-2 p-2 bg-background rounded-lg">
                          {renderContributorBadge(assessment.assessor_id)}
                          <div className="flex-1">
                            <Badge variant="secondary" className="text-xs mb-1">
                              {assessment.rating_action.replace('_', ' ')}
                            </Badge>
                            {assessment.feedback_text && (
                              <p className="text-sm text-muted-foreground">{assessment.feedback_text}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              );
            })
          )
        )}
      </div>
    </Card>
  );
};
